import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import CampaignIcon from '@mui/icons-material/Campaign';
import styled, { keyframes } from 'styled-components';
import { useDarkMode } from '../contexts/DarkModeContext';
import { usePublicNotices } from '../hooks/usePublicNotices';

const ROTATE_MS = 5000;
const MAX_ITEMS = 3;

/** 챗봇 메인 — 최신 공지 최대 3건을 순서대로 교대 표시 (글 없으면 미표시) */
const NoticeBanner: React.FC = () => {
  const { isDarkMode } = useDarkMode();
  const { items: allItems } = usePublicNotices();
  const items = allItems.slice(0, MAX_ITEMS);
  const [index, setIndex] = useState(0);
  const [timerKey, setTimerKey] = useState(0);

  useEffect(() => {
    setIndex(0);
  }, [items.length]);

  useEffect(() => {
    if (items.length <= 1) return;
    const id = window.setInterval(() => {
      setIndex((i) => (i + 1) % items.length);
    }, ROTATE_MS);
    return () => window.clearInterval(id);
  }, [items.length, timerKey]);

  const selectIndex = (i: number) => {
    setIndex(i);
    setTimerKey((k) => k + 1); // 자동 전환 타이머 리셋
  };

  const current = items[index];
  if (!current) return null;

  return (
    <Banner>
      <IconWrap $dark={isDarkMode} aria-hidden>
        <CampaignIcon fontSize="inherit" />
      </IconWrap>
      <Label $dark={isDarkMode}>공지</Label>
      <Divider $dark={isDarkMode} aria-hidden>
        |
      </Divider>
      <TitleWrap>
        <TitleLink
          key={current.id}
          $dark={isDarkMode}
          to={`/notices/${current.id}`}
          title={current.title}
        >
          {current.title}
        </TitleLink>
      </TitleWrap>
      {items.length > 1 && (
        <Dots role="tablist" aria-label="공지 선택">
          {items.map((n, i) => (
            <Dot
              key={n.id}
              type="button"
              role="tab"
              aria-selected={i === index}
              aria-label={`${i + 1}번째 공지`}
              $active={i === index}
              $dark={isDarkMode}
              onClick={() => selectIndex(i)}
            />
          ))}
        </Dots>
      )}
    </Banner>
  );
};
export default NoticeBanner;

const fadeIn = keyframes`
  from {
    opacity: 0;
    transform: translateY(0.3rem);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
`;

const Banner = styled.div`
  display: flex;
  align-items: center;
  gap: 1.2rem;
  width: 100%;
  max-width: 100%;
  min-width: 0;
  flex: 0 0 auto;
  padding: 1rem 2.4rem 0.4rem;
  overflow: hidden;
  box-sizing: border-box;
  font-family: 'Noto Sans KR', sans-serif;

  @media (max-width: 768px) {
    padding: 0.8rem 1.6rem 0.4rem;
    gap: 0.6rem;
  }
`;

const IconWrap = styled.span<{ $dark: boolean }>`
  flex: 0 0 auto;
  display: inline-flex;
  align-items: center;
  font-size: 1.8rem;
  line-height: 1;
  color: ${({ $dark, theme }) => ($dark ? '#eee' : theme.color222)};

  @media (max-width: 768px) {
    font-size: 1.6rem;
  }
`;

const Label = styled.span<{ $dark: boolean }>`
  flex: 0 0 auto;
  font-size: 1.6rem;
  font-weight: 600;
  white-space: nowrap;
  color: ${({ $dark, theme }) => ($dark ? '#eee' : theme.color222)};

  @media (max-width: 768px) {
    font-size: 1.4rem;
  }
`;

const Divider = styled.span<{ $dark: boolean }>`
  flex: 0 0 auto;
  font-size: 1.6rem;
  color: ${({ $dark, theme }) => ($dark ? 'rgba(255,255,255,0.25)' : theme.borderGrayColor)};

  @media (max-width: 768px) {
    font-size: 1.4rem;
  }
`;

const TitleWrap = styled.div`
  flex: 1 1 auto;
  min-width: 0;
  overflow: hidden;
`;

const TitleLink = styled(Link)<{ $dark: boolean }>`
  display: block;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  color: ${({ $dark, theme }) => ($dark ? '#f0f0f0' : theme.color222)};
  font-size: 1.6rem;
  font-weight: 600;
  line-height: 2.2rem;
  text-decoration: none;
  animation: ${fadeIn} 1.1s ease;

  &:hover {
    text-decoration: underline;
    color: ${({ $dark, theme }) => ($dark ? '#fff' : theme.btnColor)};
  }

  @media (max-width: 768px) {
    font-size: 1.4rem;
    line-height: 2rem;
  }
`;

const Dots = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  flex: 0 0 auto;
`;

const Dot = styled.button<{ $active: boolean; $dark: boolean }>`
  width: 0.8rem;
  height: 0.8rem;
  padding: 0;
  border: none;
  border-radius: 50%;
  cursor: pointer;
  background: ${({ $active, $dark, theme }) =>
    $active
      ? $dark
        ? '#eee'
        : theme.color222
      : $dark
        ? 'rgba(255,255,255,0.25)'
        : theme.borderGrayColor};

  &:hover {
    opacity: 0.85;
  }

  &:focus-visible {
    outline: 2px solid ${({ theme }) => theme.btnColor};
    outline-offset: 2px;
  }
`;
